import React, { useState, useRef, useEffect } from 'react'
import { DayPicker } from 'react-day-picker'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import EventsList from './event-list'
import OpeningTimes from '../opening-times'
import { CalendarDaysIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

// Format date to ISO string (YYYY-MM-DD) in local timezone
const formatDateToISO = date => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const NfcaDayPicker = ({ defaultDate }) => {
  // Initialize with provided default date or current date
  const getInitialDate = () => {
    if (defaultDate) {
      try {
        const parsedDate = new Date(defaultDate + 'T00:00:00')
        return parsedDate
      } catch (error) {
        console.error('Error parsing default date:', error)
        return new Date()
      }
    }
    return new Date()
  }
  
  const [selected, setSelected] = useState(getInitialDate())
  // State to control day picker visibility
  const [showDayPicker, setShowDayPicker] = useState(false)
  // State to track the current month being viewed in the calendar
  const [calendarMonth, setCalendarMonth] = useState(getInitialDate())
  // Reference to the date picker button
  const datePickerButtonRef = useRef(null)

  // Fetch events for the selected date
  const {
    data: instances,
    error,
    isLoading
  } = useSWR(selected ? `/whatson/${formatDateToISO(selected)}` : null, fetcher)

  // Fetch availability data for a wider range to support navigation
  const getAvailabilityRange = () => {
    // Get a range from 3 months ago to 6 months ahead to ensure we can find prev/next dates
    const start = new Date()
    start.setMonth(start.getMonth() - 3)
    const end = new Date()
    end.setMonth(end.getMonth() + 6)
    return {
      start: formatDateToISO(start),
      end: formatDateToISO(end)
    }
  }

  const availabilityRange = getAvailabilityRange()
  const {
    data: availability
  } = useSWR(
    `/events/availability?start_date=${availabilityRange.start}&end_date=${availabilityRange.end}`,
    fetcher
  )

  // Fetch availability data specifically for the current calendar month view
  const getMonthRange = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1)
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0)
    return {
      start: formatDateToISO(start),
      end: formatDateToISO(end)
    }
  }

  const monthRange = getMonthRange(calendarMonth)
  const {
    data: monthAvailability
  } = useSWR(
    `/events/availability?start_date=${monthRange.start}&end_date=${monthRange.end}`,
    fetcher
  )

  // Handle date selection and hide the picker
  const handleDateSelect = date => {
    // Only allow selection if date has events and is not in the past
    if (date && !isDateDisabled(date)) {
      setSelected(date)
      setShowDayPicker(false)
    }
  }

  // Check if a date should be disabled
  const isDateDisabled = (date) => {
    const dateString = formatDateToISO(date)
    
    // Create a new date for today at midnight for comparison
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    // Create a date at midnight for comparison
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    
    // Disable past dates (before today)
    if (compareDate < today) {
      return true
    }
    
    // Disable dates without events (including today if no events)
    if (monthAvailability?.dates_with_events) {
      return !monthAvailability.dates_with_events.includes(dateString)
    }
    
    // If availability data isn't loaded yet, don't disable
    return false
  }

  // Convert availability dates to Date objects for react-day-picker
  const datesWithEvents = monthAvailability?.dates_with_events?.map(dateStr => new Date(dateStr)) || []

  // Calculate picker position to keep it within viewport
  const getPickerPosition = () => {
    if (!datePickerButtonRef.current) return { left: 0, top: 0 }
    
    const buttonRect = datePickerButtonRef.current.getBoundingClientRect()
    const pickerWidth = 300 // Approximate picker width
    const pickerHeight = 350 // Approximate picker height
    
    let left = buttonRect.left
    let top = buttonRect.bottom + 8
    
    // Adjust horizontal position if picker would go off-screen
    if (left + pickerWidth > window.innerWidth) {
      left = window.innerWidth - pickerWidth - 16 // 16px margin
    }
    if (left < 16) {
      left = 16 // 16px margin from left edge
    }
    
    // Adjust vertical position if picker would go off-screen
    if (top + pickerHeight > window.innerHeight) {
      top = buttonRect.top - pickerHeight - 8 // Show above button instead
    }
    
    return { left, top }
  }

  // Handle clicks outside the date picker to close it
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        showDayPicker &&
        datePickerButtonRef.current &&
        !datePickerButtonRef.current.contains(event.target) &&
        !event.target.closest('.react-day-picker')
      ) {
        setShowDayPicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDayPicker])

  // Format the selected date for display
  const formattedDate = selected
    ? selected.toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'Today'

  // Helper function to find previous date with events
  const findPreviousDateWithEvents = () => {
    if (!availability?.dates_with_events || !selected) return null
    
    const currentDateStr = formatDateToISO(selected)
    const availableDates = availability.dates_with_events
      .filter(dateStr => dateStr < currentDateStr)
      .sort()
      .reverse() // Get most recent first
    
    return availableDates.length > 0 ? availableDates[0] : null
  }

  // Helper function to find next date with events
  const findNextDateWithEvents = () => {
    if (!availability?.dates_with_events || !selected) return null
    
    const currentDateStr = formatDateToISO(selected)
    const availableDates = availability.dates_with_events
      .filter(dateStr => dateStr > currentDateStr)
      .sort() // Get earliest first
    
    return availableDates.length > 0 ? availableDates[0] : null
  }

  // Format date for navigation display (e.g., "Fri 31st")
  const formatDateForNavigation = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00')
    const day = date.getDate()
    const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 != 10) * day % 10]
    
    return date.toLocaleDateString('en-GB', {
      weekday: 'short'
    }) + ` ${day}${suffix}`
  }

  const previousDate = findPreviousDateWithEvents()
  const nextDate = findNextDateWithEvents()

  return (
    <div className='font-serif text-sm w-full h-full flex flex-col rounded-lg overflow-hidden bg-white dark:bg-gray-800 text-black dark:text-white'>
      {/* Date selection header - fixed height */}
      <div className='p-2 shrink-0'>
        <div className='flex justify-between items-center gap-2'>
          {/* Previous date navigation */}
          <div className='flex-shrink-0'>
            {previousDate ? (
              <button
                onClick={() => setSelected(new Date(previousDate + 'T00:00:00'))}
                className='flex items-center text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-100 text-xs'
                title={`Previous date with events: ${formatDateForNavigation(previousDate)}`}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-3 w-3 mr-1'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 19l-7-7 7-7'
                  />
                </svg>
                {formatDateForNavigation(previousDate)}
              </button>
            ) : (
              <div className='w-12'></div>
            )}
          </div>

          {/* Current date selector */}
          <div className='relative flex-grow flex justify-center' ref={datePickerButtonRef}>
            <button
              className='flex items-center text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-100'
              onClick={() => setShowDayPicker(!showDayPicker)}
            >
              <span className='font-medium'>{formattedDate}</span>
              {showDayPicker ? (
                <ChevronUpIcon className="h-4 w-4 ml-2" />
              ) : (
                <CalendarDaysIcon className="h-4 w-4 ml-2" />
              )}
            </button>

            {/* Floating Day Picker */}
            {showDayPicker && (
              <div className='fixed z-[9999]' style={getPickerPosition()}>
                <div className='bg-white dark:bg-gray-900 p-3 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700'>
                  <div className='flex justify-between items-center mb-2'>
                    <h4 className='text-sm font-medium text-gray-900 dark:text-white'>Select a date</h4>
                    <button
                      className='text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white cursor-pointer'
                      onClick={() => {
                        const today = new Date()
                        setSelected(today)
                        setShowDayPicker(false)
                      }}
                    >
                      Today
                    </button>
                  </div>
                  <DayPicker
                    className='react-day-picker dark:text-white'
                    mode='single'
                    selected={selected}
                    onSelect={handleDateSelect}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    components={{
                      Chevron: ({ orientation }) => (
                        <span className="text-gray-700 dark:text-gray-300">
                          {orientation === 'left' ? '‹' : '›'}
                        </span>
                      )
                    }}
                    disabled={(date) => {
                      const dateString = formatDateToISO(date)
                      
                      // Create a new date for today at midnight for comparison
                      const today = new Date()
                      today.setHours(0, 0, 0, 0)
                      
                      // Create a date at midnight for comparison
                      const compareDate = new Date(date)
                      compareDate.setHours(0, 0, 0, 0)
                      
                      // Disable past dates (before today)
                      if (compareDate < today) {
                        return true
                      }
                      
                      // Disable dates without events (including today if no events)
                      if (monthAvailability?.dates_with_events) {
                        return !monthAvailability.dates_with_events.includes(dateString)
                      }
                      
                      return false
                    }}
                    modifiers={{
                      hasEvents: datesWithEvents
                    }}
                    modifiersClassNames={{
                      hasEvents: 'bg-blue-100 dark:bg-blue-500/20 border border-blue-300 dark:border-blue-500/30'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Next date navigation */}
          <div className='flex-shrink-0'>
            {nextDate ? (
              <button
                onClick={() => setSelected(new Date(nextDate + 'T00:00:00'))}
                className='flex items-center text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-100 text-xs'
                title={`Next date with events: ${formatDateForNavigation(nextDate)}`}
              >
                {formatDateForNavigation(nextDate)}
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-3 w-3 ml-1'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 5l7 7-7 7'
                  />
                </svg>
              </button>
            ) : (
              <div className='w-12'></div>
            )}
          </div>
        </div>
      </div>

      {/* Opening times component */}
      <div className='p-2 pt-0 shrink-0'>
        <OpeningTimes date={formatDateToISO(selected)} />
      </div>

      {/* Events container - scrollable with constrained height */}
      <div className='border-t border-gray-200 dark:border-gray-700 flex-grow overflow-hidden'>
        <div className='h-full overflow-y-auto'>
          <EventsList
            instances={instances}
            isLoading={isLoading}
            isError={!!error}
            selectedDate={selected}
            availability={availability}
            onDateSelect={setSelected}
          />
        </div>
      </div>
    </div>
  )
}

export default NfcaDayPicker
